import {
  TableDefinition,
  BaseTableDefinitionFactory,
  ColumnFilterType,
  ActiveUserProfilePromiseService,
  ClientPromiseLookupService,
  CcyPairPromiseLookupService,
  CurrencyPairGroupPromiseLookupService,
  ActiveUser,
  Temperature,
} from "shared/cubiq-services";
import { BfxBlotterFilter } from "../bfx-blotter-filter/bfx-blotter-filter.constants";
import {
  QueryGroupImpl,
  QueryRuleImpl,
  Condition,
  Operator,
  BigNumber,
  CurrencyPairImpl,
  CurrencyPairGroupImpl,
  CurrencyPairGroupMemberImpl,
  RequestOrderType,
  OrderType,
} from "shared/cubiq-model-multi-asset";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";
import { RowNode } from "ag-grid-community";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxSysConfigUtilService } from "shared/auth-services";

const ME_VALUE_IDENTIFIER: string = "me()";
const WILDCARD_MASK = "/***";

export const CURRENCY_GROUP_VALUE_IDENTIFIER: string = "group";

export abstract class BfxBaseBlotterFilteringService {
  protected columns: any = null;
  protected tableDefinition: TableDefinition = null;
  protected activeUser: ActiveUser = null;
  protected myBranchCode: string = null;
  protected currencyPairGroupMap: any = {};
  protected currencyPairs: Array<string> = [];
  protected wildcardCurrencyPairMap: any = {};

  constructor(
    protected tableDefinitionFactory: BaseTableDefinitionFactory,
    protected activeUserService: ActiveUserProfilePromiseService,
    protected clientLookupService: ClientPromiseLookupService,
    protected currencyPairLookupService: CcyPairPromiseLookupService = null,
    protected currencyPairGroupService: CurrencyPairGroupPromiseLookupService = null,
    protected sysConfigUtilService?: BfxSysConfigUtilService,
  ) {
    tableDefinitionFactory.defineTable().then((value: TableDefinition) => {
      this.tableDefinition = value;
      this.columns = {};
      this.tableDefinition.columns.forEach((column: BfxColDef) => {
        this.columns[column.field] = column;
      });
    });

    this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
      this.activeUser = data;
      if (this.activeUser.branchCd != null) {
        this.myBranchCode = this.activeUser.branchCd;
      }
    });

    if (this.currencyPairLookupService != null) {
      this.currencyPairLookupService.isPrimed().then(() => {
        this.currencyPairLookupService.getAll().then((values: Array<CurrencyPairImpl>) => {
          values.forEach((currencyPair: CurrencyPairImpl) => {
            let ccyPair: string = currencyPair.base + "/" + currencyPair.nonBase;
            let wildcard: string = currencyPair.base + WILDCARD_MASK;
            let values: Array<string> = this.wildcardCurrencyPairMap[wildcard];

            this.currencyPairs.push(ccyPair);
            if (values == null) {
              this.wildcardCurrencyPairMap[wildcard] = values = [];
            }
            values.push(ccyPair);

            wildcard = currencyPair.nonBase + WILDCARD_MASK;
            values = this.wildcardCurrencyPairMap[wildcard];
            if (values == null) {
              this.wildcardCurrencyPairMap[wildcard] = values = [];
            }
            values.push(ccyPair);
          });
        });
      });
    }

    if (this.currencyPairGroupService != null) {
      this.currencyPairGroupService.isPrimed().then(() => {
        this.currencyPairGroupService.getAll().then((values: Array<CurrencyPairGroupImpl>) => {
          values.forEach((group: CurrencyPairGroupImpl) => {
            let memberList: Array<string> = [];
            const memberMap = {};
            group.groupMembership.forEach((member: CurrencyPairGroupMemberImpl) => {
              const memberId = member.baseCurrencyCd + "/" + member.nonBaseCurrencyCd;
              // Expand out any group members that use wildcard syntax
              if (memberId.endsWith(WILDCARD_MASK)) {
                const wildcardValues: Array<string> = this.wildcardCurrencyPairMap[memberId];
                if (wildcardValues != null) {
                  for (const groupMember of wildcardValues) {
                    if (!memberMap[groupMember]) {
                      memberMap[groupMember] = true;
                      memberList.push(groupMember);
                    }
                  }
                }
              } else {
                if (!memberMap[memberId]) {
                  memberMap[memberId] = true;
                  memberList.push(memberId);
                }
              }
            });
            this.currencyPairGroupMap[group.id.toString()] = memberList;
          });
        });
      });
    }
  }

  public doFiltersPass(
    rowData: RowNode | CubiqTS.OrderData,
    filters: Array<BfxBlotterFilter>,
    omitRuleForNotifications = false,
    dealtMode?: boolean,
  ): boolean {
    for (let i: number = 0; i < filters.length; i++) {
      if (this.doesFilterPass(rowData, filters[i], omitRuleForNotifications, dealtMode)) {
        return true;
      }
    }

    return false;
  }

  public doesFilterPass(
    rowData: RowNode | CubiqTS.OrderData,
    filter: BfxBlotterFilter,
    omitRuleForNotifications = false,
    dealtMode?: boolean,
  ): boolean {
    let queryGroup: QueryGroupImpl = filter.queryGroup as QueryGroupImpl;
    return this.checkRules(rowData, queryGroup, omitRuleForNotifications, dealtMode);
  }

  protected checkRules(
    rowData: RowNode | CubiqTS.OrderData,
    queryGroup: QueryGroupImpl,
    omitRuleForNotifications = false,
    dealtMode?: boolean,
  ): boolean {
    // Is it market bar
    if (rowData instanceof RowNode && rowData.data != null && rowData.data.fullWidth == true) {
      return true;
    }

    let rules: CubiqTS.QueryRuleImpl[] = queryGroup.rules;
    if (rules != null) {
      for (let i: number = 0; i < rules.length; i++) {
        let result: boolean = this.checkRule(
          rowData,
          rules[i],
          dealtMode,
          omitRuleForNotifications,
        );

        if (result && queryGroup.condition === Condition.OR) {
          return true;
        }

        if (!result && queryGroup.condition === Condition.AND) {
          return false;
        }
      }
    }

    let groups: QueryGroupImpl[] = queryGroup.groups;
    if (groups != null) {
      for (let i: number = 0; i < groups.length; i++) {
        let result: boolean = this.checkRules(
          rowData,
          groups[i],
          omitRuleForNotifications,
          dealtMode,
        );

        if (result && queryGroup.condition === Condition.OR) {
          return true;
        }

        if (!result && queryGroup.condition === Condition.AND) {
          return false;
        }
      }
    }

    return queryGroup.condition === Condition.OR ? false : true;
  }

  protected checkRule(
    rowData: RowNode | CubiqTS.OrderData,
    rule: QueryRuleImpl,
    dealtMode?: boolean,
    omitRuleForNotifications = false,
  ): boolean {
    let column: BfxColDef = this.columns[rule.field];
    let orderBigNumber: BigNumber = null;

    if (column == null) {
      return true;
    }

    let preprocessedFilteredValue = this.preprocessFilteredValue(
      column,
      rowData,
      rule,
      dealtMode,
      omitRuleForNotifications,
    );
    let orderValues: any[] = Array.isArray(preprocessedFilteredValue) // extracted values from Object that is filtered
      ? Array.from(preprocessedFilteredValue)
      : Array.of(preprocessedFilteredValue);
    orderValues = [...new Set(orderValues)]; // remove duplicates

    let valuesToFilterBy: Array<any> = this.preprocessValues(column, rule); // values from filter attribute list

    // https://www.bcdfx.com/ijira/browse/OMS-34494
    // Value Date Filtering fix
    let operatorsWithTwoValues = [Operator.Between, Operator.NotBetween];
    if (
      Array.isArray(orderValues) &&
      orderValues.length === 1 &&
      CubiqTS.moment.isMoment(orderValues[0]) &&
      !isNaN(valuesToFilterBy[0])
    ) {
      let objectUnixTime = orderValues[0].format("x");
      orderValues[0] = new BigNumber(objectUnixTime); // order value
      valuesToFilterBy[0] = new BigNumber(valuesToFilterBy[0]); // rule value
      // It's a Between
      if (operatorsWithTwoValues.includes(rule.operator) && valuesToFilterBy[1] != null) {
        valuesToFilterBy[1] = new BigNumber(valuesToFilterBy[1]); // rule value
      }
    }

    // https://www.bcdfx.com/ijira/browse/OMS-34250
    // if objectValues[0] is null the conversion to BigNumber will produce an error.
    let bigNumberOperators = [
      Operator.Greater,
      Operator.GreaterOrEqual,
      Operator.Less,
      Operator.LessOrEqual,
      Operator.Between,
      Operator.NotBetween,
    ];
    if (bigNumberOperators.includes(rule.operator)) {
      if (Array.isArray(orderValues) && orderValues.length === 1 && orderValues[0] == null) {
        return false;
      } else {
        try {
          orderBigNumber = new BigNumber(orderValues as any);

          if (orderBigNumber.isNaN()) {
            return false;
          }
        } catch (ex) {
          return false;
        }
      }
    }

    switch (rule.operator) {
      case Operator.Equal:
        if (
          Array.isArray(orderValues) &&
          orderValues.length === 1 &&
          orderValues[0] instanceof CubiqTS.BigNumber &&
          Array.isArray(valuesToFilterBy) &&
          valuesToFilterBy.length === 1 &&
          valuesToFilterBy[0] instanceof CubiqTS.BigNumber
        ) {
          return orderValues[0].isEqualTo(valuesToFilterBy[0]);
        }
      case Operator.In:
        return valuesToFilterBy.some((val) => orderValues.indexOf(val) >= 0);
      case Operator.NotEqual:
        if (
          Array.isArray(orderValues) &&
          orderValues.length === 1 &&
          orderValues[0] instanceof CubiqTS.BigNumber &&
          Array.isArray(valuesToFilterBy) &&
          valuesToFilterBy.length === 1 &&
          valuesToFilterBy[0] instanceof CubiqTS.BigNumber
        ) {
          return !orderValues[0].isEqualTo(valuesToFilterBy[0]);
        }
      case Operator.NotIn:
        return orderValues.some((value) => !valuesToFilterBy.includes(value));
      case Operator.Greater:
        return new BigNumber(orderBigNumber).isGreaterThan(new BigNumber(valuesToFilterBy[0]));
      case Operator.GreaterOrEqual:
        return new BigNumber(orderBigNumber).isGreaterThanOrEqualTo(
          new BigNumber(valuesToFilterBy[0]),
        );
      case Operator.Less:
        return new BigNumber(orderBigNumber).isLessThan(new BigNumber(valuesToFilterBy[0]));
      case Operator.LessOrEqual:
        return new BigNumber(orderBigNumber).isLessThanOrEqualTo(
          new BigNumber(valuesToFilterBy[0]),
        );
      case Operator.Between: {
        let a: BigNumber = new BigNumber(orderBigNumber);
        let b: BigNumber = new BigNumber(valuesToFilterBy[0]);
        let c: BigNumber = new BigNumber(valuesToFilterBy[1]);
        return b.isGreaterThan(c)
          ? a.isLessThanOrEqualTo(b) && a.isGreaterThanOrEqualTo(c)
          : a.isLessThanOrEqualTo(c) && a.isGreaterThanOrEqualTo(b);
      }
      case Operator.NotBetween: {
        let a: BigNumber = new BigNumber(orderBigNumber);
        let b: BigNumber = new BigNumber(valuesToFilterBy[0]);
        let c: BigNumber = new BigNumber(valuesToFilterBy[1]);
        return b.isGreaterThan(c)
          ? a.isLessThan(c) || a.isGreaterThan(b)
          : a.isLessThan(b) || a.isGreaterThan(c);
      }
    }
  }

  protected preprocessFilteredValue(
    column: BfxColDef,
    rowData: RowNode | CubiqTS.OrderData,
    rule: QueryRuleImpl,
    dealtMode?: boolean,
    omitRuleForNotifications = false,
  ): string {
    let value = rowData instanceof RowNode ? rowData.data[column.field] : rowData[column.field];
    let hasDealtModeContext = typeof dealtMode === "boolean";

    if (
      value == null &&
      (rowData instanceof CubiqTS.InterventionEventImpl ||
        (rowData instanceof RowNode && rowData.data instanceof CubiqTS.InterventionEventImpl))
    ) {
      let interventionEvent: CubiqTS.InterventionEventImpl =
        rowData instanceof RowNode ? rowData.data : rowData;
      if (interventionEvent.orderData != null) {
        value = interventionEvent.orderData[column.field];
      }
    }

    if (column.getFilterValue != null) {
      return column.getFilterValue(rowData instanceof RowNode ? rowData.data : rowData);
    }

    if (
      column.valueGetter != null &&
      typeof column.valueGetter === "function" &&
      (rowData instanceof RowNode ||
        rowData instanceof CubiqTS.OrderDataImpl ||
        rowData instanceof CubiqTS.InterventionEventImpl ||
        rowData instanceof CubiqTS.DealImpl) &&
      column.columnFilterType !== ColumnFilterType.ENUM &&
      column.columnFilterType !== ColumnFilterType.COMPOSITE_ENUM &&
      column.columnFilterType !== ColumnFilterType.TRADERGROUP &&
      column.columnFilterType !== ColumnFilterType.SALESGROUP &&
      column.columnFilterType !== ColumnFilterType.ORDERSTATE &&
      (column.columnFilterType !== ColumnFilterType.NUMBER ||
        column.field === "volumePerMinuteQty") &&
      (column.columnFilterType !== ColumnFilterType.INT || column.field === "orderQty")
    ) {
      let data: any = rowData;
      if (
        hasDealtModeContext &&
        data.context != null &&
        ((dealtMode === false &&
          (OrdersTableMode.BASE === data.context.mode ||
            OrdersTableMode.NDF_BASE === data.context.mode)) ||
          (dealtMode === true &&
            (OrdersTableMode.DEALT === data.context.mode ||
              OrdersTableMode.NDF_DEALT === data.context.mode)))
      ) {
        // passthrough context if they agree
        data = rowData;
      } else if (hasDealtModeContext && data.context != null) {
        // use private context for mode disagreement but don't mutate real context!
        data = {
          data: rowData instanceof RowNode ? rowData.data : rowData,
          context: Object.assign({}, data.context, {
            mode:
              dealtMode != null && dealtMode === false
                ? OrdersTableMode.BASE
                : OrdersTableMode.DEALT,
          }),
        };
      } else if (hasDealtModeContext) {
        // generate context if unavailable
        data = {
          data: rowData instanceof RowNode ? rowData.data : rowData,
          context: {
            mode:
              dealtMode != null && dealtMode === false
                ? OrdersTableMode.BASE
                : OrdersTableMode.DEALT,
          },
        };
      } else {
        data =
          rowData instanceof RowNode
            ? rowData
            : {
                data: rowData,
                context: {
                  mode:
                    dealtMode != null && dealtMode === false
                      ? OrdersTableMode.BASE
                      : OrdersTableMode.DEALT,
                },
              };
      }

      value = column.valueGetter(data);
    }

    if (value != null && column.columnFilterType == ColumnFilterType.CURRENCY_PAIR) {
      if (this.currencyPairs.indexOf(value) < 0) {
        let split: number = value.indexOf("/");
        if (split == 3 && value.length == 7) {
          value = value.substr(4, 3) + "/" + value.substr(0, 3);
        }
      }
    } else if (
      value != null &&
      column.columnFilterType == ColumnFilterType.PERCENTAGE &&
      !Number.isNaN(value) &&
      value < 0
    ) {
      value = Math.abs(value);
    } else if (value != null && column.columnFilterType === ColumnFilterType.ENUM) {
      value = column.columnFilterData[value];
    } else if (value != null && column.columnFilterType === ColumnFilterType.COMPOSITE_ENUM) {
      value = column.columnFilterData.getData(value);
    } else if (value != null && column.columnFilterType === ColumnFilterType.DATE_TENOR) {
      let settleTenorValue =
        rowData instanceof RowNode
          ? rowData.data.settleTenor.toString()
          : rowData.settleTenor.toString();
      value = settleTenorValue;
    } else if (value != null && column.columnFilterType === ColumnFilterType.ORDERSTATE) {
      value = CubiqTS.OrderState[value];
    }

    if (value instanceof BigNumber) {
      value = (value as BigNumber).toString();
    } else if (typeof value === "number") {
      value = (value as number).toString();
    } else if (value instanceof CubiqTS.Long) {
      value = value.toString();
    }

    return value;
  }

  protected preprocessValues(column: BfxColDef, rule: QueryRuleImpl): Array<any> {
    let values: Array<any> = rule.values;

    switch (column.columnFilterType) {
      case ColumnFilterType.ORDERTYPE:
        let extras = [];
        rule.values.forEach((v: string) => {
          // if we have a fulter for MKT  - we need to add the other Market Types
          if (RequestOrderType.MARKET.toString() == v) {
            extras = extras.concat(OrderType.MARKET);
            extras = extras.concat(OrderType.MARKET_FOK);
            extras = extras.concat(OrderType.MARKET_IOC);
          }

          if (RequestOrderType.CALL.toString() == v) {
            extras = extras.concat(this.sysConfigUtilService.manualExecutionCallNameText);
          }
        });
        values = values.concat(extras);
        break;
      case ColumnFilterType.USER:
        values = rule.values.map((v: string) =>
          ME_VALUE_IDENTIFIER == v ? this.activeUser.userCode : v,
        );
        break;
      case ColumnFilterType.TRADERGROUP:
        {
          let index: number = rule.values.indexOf(ME_VALUE_IDENTIFIER);
          if (index >= 0) {
            values = rule.values.slice(0, rule.values.length);
            values.splice(index, 1);
            values = values.concat(this.activeUser.tradingGroupsIds.map((v) => v + ""));
          }
        }
        break;
      case ColumnFilterType.SALESGROUP:
        {
          let index: number = rule.values.indexOf(ME_VALUE_IDENTIFIER);
          if (index >= 0) {
            values = rule.values.slice(0, rule.values.length);
            values.splice(index, 1);
            values = values.concat(this.activeUser.salesGroupsIds).map((v) => v + "");
          }
        }
        break;
      case ColumnFilterType.BRANCH:
        {
          let index: number = rule.values.indexOf(ME_VALUE_IDENTIFIER);
          if (index >= 0) {
            values = rule.values.slice(0, rule.values.length);
            values.splice(index, 1);
            values = values.concat(this.myBranchCode);
          }
        }
        break;
      case ColumnFilterType.BOOLEAN:
        {
          values = [rule.values[0] == "Yes"];
        }
        break;
      case ColumnFilterType.CURRENCY_PAIR:
        {
          let groupEnd = 1;
          values = [];
          const valueMap = {};

          if (rule.values.length > 1 && rule.values[0] == CURRENCY_GROUP_VALUE_IDENTIFIER) {
            let memberList: Array<string> = [];

            rule.values.some((value: string, i) => {
              if (value === CURRENCY_GROUP_VALUE_IDENTIFIER) {
                groupEnd = i + 1;
                return groupEnd != 1;
              }

              let members: Array<string> = this.currencyPairGroupMap[value];
              if (members != null) {
                memberList = memberList.concat(members);
              }

              return false;
            });

            // Add unique members to values
            for (const memberId of memberList) {
              if (!valueMap[memberId]) {
                valueMap[memberId] = true;
                values.push(memberId);
              }
            }
          }

          if (groupEnd != 1) {
            rule.values.slice(groupEnd, rule.values.length).forEach((v: string) => {
              if (v.endsWith(WILDCARD_MASK)) {
                let wildcardValues: Array<string> = this.wildcardCurrencyPairMap[v];
                if (wildcardValues != null) {
                  for (const memberId of wildcardValues) {
                    if (!valueMap[memberId]) {
                      valueMap[memberId] = true;
                      values.push(memberId);
                    }
                  }
                }
              } else {
                if (!valueMap[v]) {
                  valueMap[v] = true;
                  values.push(v);
                }
              }
            });
          }
        }
        break;
      default:
        break;
    }

    return [...values];
  }
}
